import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, InfiniteScrollCustomEvent, IonInfiniteScroll, IonModal, ModalController, PopoverController } from '@ionic/angular';
import { Payment } from 'src/app/models/payment';
import { ApiProviderService } from 'src/app/services/api-provider.service';
import { PaymentServiceService } from 'src/app/services/payment-service.service';
import { UserService } from 'src/app/services/user.service';
import { FormPaymentComponent } from '../form-payment/form-payment.component';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-payments-list',
  templateUrl: './payments-list.component.html',
  styleUrls: ['./payments-list.component.scss'],
})
export class PaymentsListComponent implements OnInit {
  @ViewChild(IonModal) modal: IonModal;

  payments: Payment[] = [];
  filteredPayments: Payment[] = [];
  coachId: string;
  searchTerm$ = new Subject<string>();
  searchTerm: string;
  isInactive: boolean = false;
  popoverOpen = false;
  popoverEvent: Event;
  selectedPayment: any;
  loaderVisibility = false

  // Nouvelles propriétés pour les filtres
  filterPopoverOpen = false;
  filterPopoverEvent: Event;
  selectedAmountFilter: string = 'all';
  selectedDateFilter: string = 'all';
  selectedClientFilter: string = 'all';
  selectedPaymentMethodFilter: string = 'all';
  uniqueClients: any[] = [];
  startDate: string;
  endDate: string;

  constructor(
    private userService: UserService,
    private paymentService: PaymentServiceService,
    private modalCtrl: ModalController,
    private router: Router,
    private alertController: AlertController,
    private popoverController: PopoverController
  ) { 


  }

  ngOnInit() {
    this.coachId = this.userService.getUser().id;

    this.paymentService.payments$.subscribe((updatedPayments) => {
      this.payments = updatedPayments;
      this.updateUniqueClients();
      this.filterPayments(this.searchTerm);
    });

    this.paymentService.upcomingPayments$.subscribe((upcomingPayments) => {
      if (this.isInactive) {
        this.filteredPayments = upcomingPayments;
        this.filterPayments(this.searchTerm);
      }
    });

    this.paymentService.completedPayments$.subscribe((completedPayments) => {
      if (!this.isInactive) {
        this.filteredPayments = completedPayments;
        this.filterPayments(this.searchTerm);
      }
    });

    this.searchTerm$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe((searchTerm) => {
      this.filterPayments(searchTerm);
    });

    console.log(this.filterPayments)
  }

  cancel() {
    this.modal.dismiss(null, 'cancel');
  }

  async openModal(title: string) {
    const modal = await this.modalCtrl.create({
      component: FormPaymentComponent,
      componentProps: {
        title: title,
        showClientField: true
      }
    });
    modal.present();
    const { data, role } = await modal.onWillDismiss();
  }

  search(event: any): void {
    this.searchTerm = event.detail.value;
    this.searchTerm$.next(this.searchTerm);
  }

  filterPayments(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.applyFilters();
  }

  async deletePayment(payment: Payment) {
    this.paymentService.deletePayment(payment);
    this.popoverOpen = false;
  }

  async confirmDeletePayment(payment: Payment) {
    const alert = await this.alertController.create({
      header: 'Confirmation',
      message: 'Êtes-vous sûr de vouloir supprimer ce paiement ?',
      buttons: [
        {
          text: 'Non',
          role: 'cancel',
          handler: () => {
            payment.showListBtnBox = true;
          }
        }, {
          text: 'Ok',
          role: 'confirm',
          handler: () => {
            this.deletePayment(payment);
          }
        }
      ]
    });
    await alert.present();
  }

  toggleStatus(isActiveList: string) {
    this.isInactive = (isActiveList === 'Inactif');
    this.filterPayments(this.searchTerm);
  }

  async validUpcomingPayment(payment: Payment) {
    const alert = await this.alertController.create({
      header: 'Confirmation',
      message: 'Êtes-vous sûr de vouloir valider ce paiement ?',
      buttons: [
        {
          text: 'Non',
          role: 'cancel',
          handler: () => {
            payment.showListBtnBox = true;
          }
        }, {
          text: 'Oui',
          role: 'confirm',
          handler: () => {
            payment.type = 0
            this.paymentService.validUpcomingPayment(payment)
            this.popoverController.dismiss();
          }
        }
      ]
    });
    await alert.present();

  }

  async loadData(event?: any) {
    this.loaderVisibility = true;
    this.paymentService.currentPage += 1;
    await this.paymentService.getPayments(this.coachId);
    this.loaderVisibility = false;
    if (event) {
      event.target.complete();
    }
  }


  //PopOver
  openPopover(ev: Event, payment: any) {
    this.popoverEvent = ev;
    this.selectedPayment = payment;
    this.popoverOpen = true;
  }

  // Nouvelles méthodes pour les filtres
  openFilterPopover(ev: Event) {
    this.filterPopoverEvent = ev;
    this.filterPopoverOpen = true;
  }

  updateUniqueClients() {
    const clients = new Set();
    this.payments.forEach(payment => {
      if (payment.client) {
        clients.add(JSON.stringify({
          id: payment.client.id,
          firstName: payment.client.firstName,
          name: payment.client.name
        }));
      }
    });
    this.uniqueClients = Array.from(clients).map(client => JSON.parse(client as string));
  }

  onDateFilterChange() {
    if (this.selectedDateFilter !== 'custom') {
      this.startDate = null;
      this.endDate = null;
    }
    this.applyFilters();
  }

  applyFilters() {
    let filtered = this.isInactive 
      ? this.paymentService.upcomingPaymentsSubject.getValue().slice()
      : this.paymentService.completedPaymentsSubject.getValue().slice();

    // Filtre par montant
    if (this.selectedAmountFilter !== 'all') {
      filtered = filtered.filter(payment => {
        const amount = payment.montantPaye;
        switch (this.selectedAmountFilter) {
          case 'high':
            return amount > 100;
          case 'medium':
            return amount >= 50 && amount <= 100;
          case 'low':
            return amount < 50;
          default:
            return true;
        }
      });
    }

    // Filtre par date
    if (this.selectedDateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

      filtered = filtered.filter(payment => {
        const paymentDate = new Date(payment.datePaiement);
        
        if (this.selectedDateFilter === 'custom') {
          if (!this.startDate || !this.endDate) return true;
          
          const start = new Date(this.startDate);
          start.setHours(0, 0, 0, 0);
          
          const end = new Date(this.endDate);
          end.setHours(23, 59, 59, 999);

          return paymentDate >= start && paymentDate <= end;
        }

        switch (this.selectedDateFilter) {
          case 'today':
            return paymentDate >= today;
          case 'week':
            return paymentDate >= weekStart;
          case 'month':
            return paymentDate >= monthStart;
          default:
            return true;
        }
      });
    }

    // Filtre par client
    if (this.selectedClientFilter !== 'all') {
      filtered = filtered.filter(payment => 
        payment.client && payment.client.id === this.selectedClientFilter
      );
    }

    // Filtre par moyen de paiement
    if (this.selectedPaymentMethodFilter !== 'all') {
      filtered = filtered.filter(payment => 
        payment.paymentMethod === this.selectedPaymentMethodFilter
      );
    }

    // Appliquer le filtre de recherche existant
    if (this.searchTerm) {
      const lowerCaseTerm = this.searchTerm.toLowerCase();
      filtered = filtered.filter(payment =>
        payment.client.name?.toLowerCase().includes(lowerCaseTerm) ||
        payment.client.firstName?.toLowerCase().includes(lowerCaseTerm)
      );
    }

    this.filteredPayments = filtered;
  }

  hasActiveFilters(): boolean {
    return this.selectedAmountFilter !== 'all' || 
           this.selectedDateFilter !== 'all' || 
           this.selectedClientFilter !== 'all' ||
           this.selectedPaymentMethodFilter !== 'all';
  }

  getAmountFilterLabel(): string {
    switch (this.selectedAmountFilter) {
      case 'high':
        return 'Plus de 100€';
      case 'medium':
        return '50-100€';
      case 'low':
        return 'Moins de 50€';
      default:
        return '';
    }
  }

  getDateFilterLabel(): string {
    if (this.selectedDateFilter === 'custom') {
      if (!this.startDate || !this.endDate) return 'Période personnalisée';
      const start = new Date(this.startDate).toLocaleDateString('fr-FR');
      const end = new Date(this.endDate).toLocaleDateString('fr-FR');
      return start === end ? `Le ${start}` : `Du ${start} au ${end}`;
    }

    switch (this.selectedDateFilter) {
      case 'today':
        return "Aujourd'hui";
      case 'week':
        return 'Cette semaine';
      case 'month':
        return 'Ce mois';
      default:
        return '';
    }
  }

  getSelectedClientName(): string {
    const client = this.uniqueClients.find(c => c.id === this.selectedClientFilter);
    return client ? `${client.firstName} ${client.name}` : '';
  }

  getPaymentMethodLabel(): string {
    switch (this.selectedPaymentMethodFilter) {
      case 'carte bancaire':
        return 'Carte bancaire';
      case 'espece':
        return 'Espèces';
      case 'virement':
        return 'Virement';
      case 'cheque':
        return 'Chèque';
      default:
        return '';
    }
  }

  clearAmountFilter() {
    this.selectedAmountFilter = 'all';
    this.applyFilters();
  }

  clearDateFilter() {
    this.selectedDateFilter = 'all';
    this.startDate = null;
    this.endDate = null;
    this.applyFilters();
  }

  clearClientFilter() {
    this.selectedClientFilter = 'all';
    this.applyFilters();
  }

  clearPaymentMethodFilter() {
    this.selectedPaymentMethodFilter = 'all';
    this.applyFilters();
  }
}
